import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Service, { isServiceReady } from '../services/Service';
import UserService from '../services/UserService';
import { REGEX_EMAIL } from '../services/models/user';
import { Field } from '../types/common';
import { useMutation } from '@tanstack/react-query';
import { Button, Input } from '@follow/ui';

const ForgotPassword = () => {
  const [email, setEmail] = useState<Field>({ value: '' });
  const [submitted, setSubmitted] = useState(false);

  const navigate = useNavigate();

  const forgotPasswordMutation = useMutation({
    mutationFn: ({ email }: { email: string }) =>
      UserService.forgotPassword(email),
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: () => {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    },
  });

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail({ value: e.target.value });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const form = event.currentTarget;
      form.requestSubmit();
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!forgotPasswordMutation.isPending && email.value && REGEX_EMAIL.test(email.value)) {
      forgotPasswordMutation.mutate({ email: email.value });
    }
  };

  useEffect(() => {
    if (!isServiceReady) {
      Service.init(() => {
        localStorage.removeItem('token');
        navigate('/login');
      });
    }
  }, [navigate]);

  return (
    <div className="flex min-h-screen bg-off-white overscroll-none overflow-hidden">
      {/* LEFT PANEL */}
      <div className="w-full lg:w-[35%] bg-secondary-900 flex flex-col">
        <div className="p-8">
          <a href={process.env.REACT_APP_LANDING_URL || '/'}>
            <img src="/logo_full_light.svg" alt="Follow" className="h-7 w-auto" />
          </a>
        </div>

        <div className="flex-1 flex items-center justify-center px-5 lg:px-16">
          {submitted ? (
            <div className="w-full max-w-[448px]">
              <h1 className="text-white/80 typo-h1">Email envoyé</h1>
              <p className="text-white/70 typo-body-lg mt-2">
                Si un compte existe avec cet email, un lien de réinitialisation a été envoyé. Vérifiez votre boîte de réception.
              </p>
              <div className="mt-8">
                <Link to="/login" className="text-white/90 font-medium hover:underline typo-small">
                  Retour à la connexion
                </Link>
              </div>
            </div>
          ) : (
            <form className="w-full max-w-[448px]" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
              <h1 className="text-white/80 typo-h1">Mot de passe oublié</h1>
              <p className="text-white/70 typo-body-lg mt-2">Entrez votre email pour recevoir un lien de réinitialisation</p>

              {/* Email */}
              <div className="mt-8">
                <label className="block text-white/70 typo-small mb-2" htmlFor="email">
                  Email
                </label>
                <Input
                  tone="light"
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email.value}
                  onChange={handleEmailChange}
                />
              </div>

              {/* Submit */}
              <div className="mt-8">
                <Button
                  type="submit"
                  variant="primary"
                  tone="light"
                  size="lg"
                  className="w-full"
                  disabled={!email.value || !REGEX_EMAIL.test(email.value)}
                  loading={forgotPasswordMutation.isPending}
                >
                  Envoyer le lien
                </Button>
              </div>

              {/* Back to login */}
              <p className="mt-6 text-center text-white/70 typo-small">
                <Link to="/login" className="text-white/90 font-medium hover:underline">
                  Retour à la connexion
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="hidden lg:flex lg:w-[65%] relative bg-secondary-900 border-l border-secondary-800 overflow-hidden lg:flex-col items-center justify-center">
        <img
          src="/signin-background.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/15" />

        <div className="relative z-10 flex flex-col justify-center items-center h-full w-full gap-12">
          <h2 className="text-white typo-display text-center leading-tight">
            <span className="block">{'Transformez vos stations en'}</span>
            <span className="block text-primary-500">{' produits connectés'}</span>
          </h2>
          <p className="text-white typo-h3 text-center">
            Une solution intégrée pour piloter vos stations à distance.
          </p>

          <div className="flex justify-end -mt-32">
            <img
              src="/signin-lock.png"
              alt="Station Follow"
              className="w-[580px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
